import { t } from './i18n';
import React, { useState, useEffect } from 'react';
import { api } from './api';

const COLORS = {
  primary: '#1a4d8f',
  primaryDark: '#0d3163',
  accent: '#d4af37',
  bg: '#f7f8fa',
  card: '#ffffff',
  text: '#1f2937',
  muted: '#6b7280',
  border: '#e5e7eb',
  danger: '#dc2626',
  success: '#059669',
};

function formatMoney(amount, currency = 'EUR') {
  if (amount == null) return '';
  const symbols = { EUR: '€', USD: '$', GBP: '£', ILS: '₪', CHF: 'CHF', RUB: '₽' };
  const s = symbols[currency] || currency + ' ';
  return `${s}${Number(amount).toFixed(2)}`;
}

export default function MenuOrder({ establishment, user }) {
  const [items, setItems] = useState([]);
  const [cart, setCart] = useState({}); // { itemId: qty }
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderForm, setOrderForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!establishment?.id) return;
    api.menuItems(establishment.id).then(setItems).catch(() => setItems([]));
  }, [establishment?.id]);

  if (items.length === 0) return null;

  // Group items by category
  const groups = new Map();
  for (const it of items) {
    const cat = it.category || 'Menu';
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat).push(it);
  }

  const cartItems = items.filter((it) => cart[it.id] > 0);
  const subtotal = cartItems.reduce((sum, it) => sum + (parseFloat(it.price) || 0) * cart[it.id], 0);
  const currency = items[0]?.currency || 'EUR';
  const cartCount = Object.values(cart).reduce((s, q) => s + q, 0);

  const addOne = (id) => setCart({ ...cart, [id]: (cart[id] || 0) + 1 });
  const removeOne = (id) => {
    const next = { ...cart };
    if ((next[id] || 0) <= 1) delete next[id];
    else next[id] -= 1;
    setCart(next);
  };

  const startOrder = () => {
    setOrderForm({
      customer_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '',
      customer_email: user?.email || '',
      customer_phone: '',
      delivery_address: '',
      pickup_date: '',
      notes: '',
    });
    setShowOrderForm(true);
  };

  const submitOrder = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const orderItems = cartItems.map((it) => ({ item_id: it.id, qty: cart[it.id] }));
      await api.placeOrder(establishment.id, { ...orderForm, items: orderItems });
      setSuccess(true);
      setCart({});
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ background: 'white', padding: 24, borderRadius: 12, marginTop: 20, border: `1px solid ${COLORS.border}` }}>
      <h3 style={{ marginTop: 0, color: COLORS.primary }}>{t('🍽️ Menu')}</h3>

      {[...groups.entries()].map(([cat, list]) => (
        <div key={cat} style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 14, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, paddingBottom: 4, borderBottom: `1px solid ${COLORS.border}` }}>
            {cat}
          </h4>
          {list.map((it) => {
            const qty = cart[it.id] || 0;
            return (
              <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px dashed ${COLORS.border}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: COLORS.text }}>{it.name}</div>
                  {it.description && <div style={{ fontSize: 13, color: COLORS.muted, marginTop: 2 }}>{it.description}</div>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  {it.price != null && (
                    <div style={{ fontWeight: 700, color: COLORS.primary, minWidth: 60, textAlign: 'right' }}>
                      {formatMoney(it.price, it.currency)}
                    </div>
                  )}
                  {it.price != null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {qty > 0 && (
                        <>
                          <button onClick={() => removeOne(it.id)} style={{ width: 28, height: 28, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 4, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>−</button>
                          <span style={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>{qty}</span>
                        </>
                      )}
                      <button onClick={() => addOne(it.id)} style={{ width: 28, height: 28, background: COLORS.primary, border: 'none', color: 'white', borderRadius: 4, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>+</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* Cart footer */}
      {cartCount > 0 && !showOrderForm && (
        <div style={{ position: 'sticky', bottom: 0, background: COLORS.primary, color: 'white', marginTop: 16, padding: 16, borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 -4px 12px rgba(0,0,0,0.15)' }}>
          <div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>{cartCount} {t('item')}{cartCount !== 1 ? 's' : ''} {t('in cart')}</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{t('Subtotal:')} {formatMoney(subtotal, currency)}</div>
          </div>
          <button onClick={startOrder} style={{ background: 'white', color: COLORS.primary, border: 'none', padding: '10px 18px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
            {t('Send Order →')}
          </button>
        </div>
      )}

      {/* Order form */}
      {showOrderForm && !success && (
        <div style={{ marginTop: 16, padding: 16, background: COLORS.bg, borderRadius: 8 }}>
          <h4 style={{ marginTop: 0, color: COLORS.primary }}>{t('📨 Send Your Order')}</h4>
          <p style={{ fontSize: 13, color: COLORS.muted, marginTop: 0 }}>
            {t('Your order will be sent to')} <strong>{establishment.name}</strong>. {t("They'll contact you to confirm.")}
          </p>
          {error && <div style={{ background: '#fee2e2', color: COLORS.danger, padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <form onSubmit={submitOrder}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted }}>{t('Your Name *')}</label>
                <input style={{ width: '100%', padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 6, boxSizing: 'border-box' }} value={orderForm.customer_name} onChange={(e) => setOrderForm({ ...orderForm, customer_name: e.target.value })} required />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted }}>{t('Your Email *')}</label>
                <input type="email" style={{ width: '100%', padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 6, boxSizing: 'border-box' }} value={orderForm.customer_email} onChange={(e) => setOrderForm({ ...orderForm, customer_email: e.target.value })} required />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted }}>{t('Phone')}</label>
                <input style={{ width: '100%', padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 6, boxSizing: 'border-box' }} value={orderForm.customer_phone} onChange={(e) => setOrderForm({ ...orderForm, customer_phone: e.target.value })} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted }}>{t('Pickup / Date')}</label>
                <input type="date" style={{ width: '100%', padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 6, boxSizing: 'border-box' }} value={orderForm.pickup_date} onChange={(e) => setOrderForm({ ...orderForm, pickup_date: e.target.value })} />
              </div>
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, display: 'block', marginTop: 8 }}>{t('Delivery Address (optional)')}</label>
            <input style={{ width: '100%', padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 6, boxSizing: 'border-box' }} value={orderForm.delivery_address} onChange={(e) => setOrderForm({ ...orderForm, delivery_address: e.target.value })} placeholder={t("Leave blank for pickup")} />
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, display: 'block', marginTop: 8 }}>{t('Notes')}</label>
            <textarea style={{ width: '100%', padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 6, boxSizing: 'border-box', minHeight: 60, fontFamily: 'inherit' }} value={orderForm.notes} onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })} placeholder={t("Allergies, preferences, etc.")} />

            {/* Cart summary */}
            <div style={{ background: 'white', padding: 12, borderRadius: 6, marginTop: 12, border: `1px solid ${COLORS.border}` }}>
              <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 14 }}>{t('Your Order')}</div>
              {cartItems.map((it) => (
                <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                  <span>{cart[it.id]}× {it.name}</span>
                  <span>{formatMoney((parseFloat(it.price) || 0) * cart[it.id], currency)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: `1px solid ${COLORS.border}`, fontWeight: 700 }}>
                <span>{t('Subtotal')}</span>
                <span>{formatMoney(subtotal, currency)}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button type="submit" disabled={submitting} style={{ flex: 1, background: COLORS.primary, color: 'white', border: 'none', padding: '12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                {submitting ? t('Sending...') : t('📨 Send Order')}
              </button>
              <button type="button" onClick={() => setShowOrderForm(false)} style={{ background: 'white', color: COLORS.primary, border: `1px solid ${COLORS.primary}`, padding: '12px 18px', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                {t('Cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {success && (
        <div style={{ marginTop: 16, padding: 16, background: '#d1fae5', borderRadius: 8, color: '#065f46' }}>
          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 16 }}>{t('✓ Order Sent!')}</div>
          <div style={{ fontSize: 14 }}>
            {t('Your order has been sent to')} {establishment.name}. {t("They'll contact you to confirm details.")}
          </div>
        </div>
      )}
    </div>
  );
}
